
import React, { useState, useReducer } from 'react';
import { RotateCcw } from 'lucide-react';
import KeywordsSection from './JobFilters/KeywordsSection';
import BudgetSection from './JobFilters/BudgetSection';
import ClientDetailsSection from './JobFilters/ClientDetailsSection';
import SourcesSection from './JobFilters/SourcesSection';
import { SECTION_DEFAULTS, DEFAULT_FILTERS } from '../../constants/defaultFilters.js';

// The three collapsible-section toggles are one related UI concern (which
// accordion panels are open), so they share a reducer instead of three
// separate useState calls. showExclude / prevExclude stay as useState: they
// implement the adjust-state-during-render sync with the excludeKeywords prop
// and must not be folded into the reducer.
const initialExpanded = { keywords: false, jobTerms: false, clientDetails: false };

function expandedReducer(state, section) {
    return { ...state, [section]: !state[section] };
}

export default function JobFilters({ filters, onChange }) {
    const [showExclude, setShowExclude] = useState(() => (filters.excludeKeywords?.length ?? 0) > 0);
    const [prevExclude, setPrevExclude] = useState(filters.excludeKeywords);
    const [expanded, toggleExpanded] = useReducer(expandedReducer, initialExpanded);

    if (filters.excludeKeywords !== prevExclude) {
        setPrevExclude(filters.excludeKeywords);
        if ((filters.excludeKeywords?.length ?? 0) > 0) setShowExclude(true);
    }

    const handleToggleExclude = () => {
        if (showExclude) {
            // Toggling OFF: Clear exclude keywords
            onChange({ ...filters, excludeKeywords: [] });
        }
        setShowExclude(!showExclude);
    };

    // Reset one accordion section to its defaults by merging its default slice
    // back over the live filters; SECTION_DEFAULTS is the single source of truth.
    const clearSection = (section) => onChange({ ...filters, ...SECTION_DEFAULTS[section] });
    const clearAll = () => {
        onChange(DEFAULT_FILTERS);
        setShowExclude(false);
    };

    return (
        <div className="w-80 flex-shrink-0 space-y-4">
            {/* Sidebar header + global reset */}
            <div className="flex items-center justify-between px-1">
                <span className="font-semibold text-gray-700">Filters</span>
                <button
                    type="button"
                    onClick={clearAll}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 focus:outline-none transition-colors"
                >
                    <RotateCcw size={13} />
                    Clear all
                </button>
            </div>

            {/* Keywords Section */}
            <KeywordsSection
                filters={filters}
                onChange={onChange}
                isExpanded={expanded.keywords}
                onToggleExpanded={() => toggleExpanded('keywords')}
                showExclude={showExclude}
                onToggleExclude={handleToggleExclude}
                onClear={() => { clearSection('keywords'); setShowExclude(false); }}
            />

            {/* Job Terms Section */}
            <BudgetSection
                filters={filters}
                onChange={onChange}
                isExpanded={expanded.jobTerms}
                onToggleExpanded={() => toggleExpanded('jobTerms')}
                onClear={() => clearSection('jobTerms')}
            />

            {/* Client Details Section */}
            <ClientDetailsSection
                filters={filters}
                onChange={onChange}
                isExpanded={expanded.clientDetails}
                onToggleExpanded={() => toggleExpanded('clientDetails')}
                onClear={() => clearSection('clientDetails')}
            />

            {/* Sources Section */}
            <SourcesSection
                filters={filters}
                onChange={onChange}
                onClear={() => clearSection('sources')}
            />
        </div >
    );
}
